/**
 * Loops for the plains kit's farm, street, shrine and garden props (props4.ts): the water wheel turns, the prayer
 * wheel spins slowly, the gong swings, hens peck and pigs root (painted per frame at their source: `LOOPS4`); trees
 * sway (willow strands most, the pine least) and the plum sheds petals, the wishing tree's ribbons stir, the notice
 * board's papers and lanterns and the scarecrow's rags flutter, the forge's coals flicker under rising smoke, water in
 * the trough, the ditch and under the wheel glints. Rects are in each still sprite (kit/propAnim.ts).
 */
import { registerPropAnim } from './propAnim';
import { LOOPS4 } from './props4';

registerPropAnim('waterWheel', {
  frames: 8,
  period: 4,
  make: LOOPS4.waterWheel,
  ops: [{ op: 'shimmer', rect: [12, 58, 62, 84], period: 9 }],
});
registerPropAnim('prayerWheel', { frames: 3, period: 1.8, make: LOOPS4.prayerWheel, ops: [] });
registerPropAnim('gongStand', { frames: 6, period: 3.2, make: LOOPS4.gongStand, ops: [] });
registerPropAnim('henCoop', { frames: 6, period: 2.6, make: LOOPS4.henCoop, ops: [] });
registerPropAnim('pigPen', { frames: 6, period: 3.6, make: LOOPS4.pigPen, ops: [] });

// The strands only (green, or their dark rim): the trunk behind them stays put.
const strand = (r: number, g: number, b: number): boolean => g > r + 6 || (r < 40 && g < 40 && b < 45);
registerPropAnim('willowTree', {
  frames: 6,
  period: 3.6,
  pad: [2, 0, 2, 0],
  ops: [
    { op: 'bend', rect: [0, 0, 96, 56], axis: 'x', pin: 'bottom', amp: 1 },
    { op: 'bend', rect: [14, 46, 96, 104], axis: 'x', pin: 'top', amp: 2, wave: 26, key: strand },
  ],
});
registerPropAnim('plumTree', {
  frames: 8,
  period: 4.4,
  ops: [
    { op: 'bend', rect: [0, 0, 80, 52], axis: 'x', pin: 'bottom', amp: 1, pow: 1.5 },
    { op: 'petals', rect: [8, 44, 72, 94], n: 4, col: ['#e096aa', '#f6c8d2'] },
  ],
});
registerPropAnim('pineTree', {
  frames: 6,
  period: 4.6,
  ops: [{ op: 'bend', rect: [0, 0, 70, 80], axis: 'x', pin: 'bottom', amp: 1, pow: 2 }],
});
const ribbon = (r: number, g: number, b: number): boolean => (r > g + 60 && r > 120) || (r > 150 && g > 90 && b < 70 && r > g + 25);
registerPropAnim('wishingTree', {
  frames: 6,
  period: 3.4,
  ops: [
    { op: 'bend', rect: [0, 0, 84, 56], axis: 'x', pin: 'bottom', amp: 1, pow: 1.5 },
    { op: 'bend', rect: [14, 24, 72, 72], axis: 'x', pin: 'top', amp: 1, wave: 10, key: ribbon, backfill: true },
  ],
});

registerPropAnim('forge', {
  frames: 6,
  period: 2.4,
  pad: [0, 18, 0, 0],
  ops: [
    { op: 'flicker', rect: [32, 34, 54, 56], key: 'warm', band: 2, cycles: 3 },
    { op: 'smoke', at: [38, 6], rise: 20, puffs: 3 },
  ],
});
const paper = (r: number, g: number, b: number): boolean => r * 0.3 + g * 0.59 + b * 0.11 >= 160;
registerPropAnim('noticeBoard', {
  frames: 6,
  period: 2.8,
  ops: [
    { op: 'bend', rect: [21, 24, 41, 53], axis: 'x', pin: 'top', amp: 1, wave: 12, key: paper, backfill: true },
    { op: 'bend', rect: [15, 18, 21, 27], axis: 'x', pin: 'top', amp: 1 },
    { op: 'bend', rect: [43, 26, 50, 35], axis: 'x', pin: 'top', amp: 1, wave: -20 },
  ],
});
registerPropAnim('scarecrow', {
  frames: 6,
  period: 2.2,
  pad: [1, 0, 1, 0],
  ops: [
    { op: 'bend', rect: [16, 45, 38, 54], axis: 'x', pin: 'top', amp: 1.2, wave: 10, stretch: true },
    { op: 'bend', rect: [14, 29, 21, 38], axis: 'x', pin: 'top', amp: 1, backfill: true },
    { op: 'bend', rect: [38, 29, 47, 38], axis: 'x', pin: 'top', amp: 1, wave: -20, backfill: true },
  ],
});
registerPropAnim('waterTrough', { frames: 6, period: 2.2, ops: [{ op: 'shimmer', rect: [22, 6, 50, 24], period: 9 }] });
registerPropAnim('ditch', { frames: 6, period: 2.2, ops: [{ op: 'shimmer', rect: [0, 0, 68, 36], period: 11 }] });
